<template>
  <swipe-out ref="swipeOutRef" :is-disabled="disableSwipe || disabled" class="w-full">
    <template v-if="allowDiscount && !disabled" #left>
      <div
        class="w-14 h-full flex items-center justify-center px-3 bg-g-500 cursor-pointer"
        @click="showDiscount = true"
      >
        <l-icon name="discount" size="small" class="text-n-0" />
      </div>
    </template>
    <template #default>
      <div
        class="w-full flex justify-center items-center px-4 min-h-14 py-2 text-n-0 font-body"
        :class="[
          disabled ? 'cursor-default' : 'cursor-pointer',
          {
            'pointer-events-none': disabled,
            'opacity-60': !productIsPaid && productIsBilled,
            'opacity-30': productIsPaid,
            'bg-n-600': isSelected
          }
        ]"
        @click="() => !disabled && $emit('selected')"
      >
        <div class="w-full flex justify-center items-start gap-2">
          <template v-if="showFullQuantity">
            <quantity-selector
              v-if="showQuantitySelector && editingQuantity"
              :quantity="product.quantity"
              :is-maxed="productOutOfStock"
              @click-outside="editingQuantity = false"
              @update:quantity="updateQuantity"
            />
            <div
              v-else
              class="w-5 h-full text-center font-heading text-sm shrink-0"
              @click.stop="editingQuantity = true"
            >
              {{ product.quantity }}
            </div>
          </template>

          <div class="flex justify-between items-start w-full gap-1">
            <div class="flex flex-col font-heading text-sm">
              <div>{{ product.name }}</div>
              <div class="text-n-200">
                {{ description }}
              </div>
              <div v-if="showComments && product.comments" class="text-n-200">
                + {{ product.comments }}
              </div>
              <div v-if="showSentToKitchen && sentTime" class="text-n-200">
                {{ t('ordering.sent') }} {{ format(sentTime, 'HH:mm') }} ({{ product.course }})
              </div>
              <div v-if="product.discountType === '2x1'" class="text-g-500 text-sm">
                <!-- eslint-disable-next-line -->
                {{ '2x1' }}
                <span v-if="product.quantity > 1"> ({{ product.discountAmount }}) </span>
              </div>
            </div>
            <div class="font-bold font-text text-xs flex items-center gap-3">
              <div v-if="showPrice" class="flex flex-col">
                <div v-if="product.discountType" class="font-body text-xs line-through">
                  <span>
                    {{ currency(product.fullPrice * (showFullQuantity ? product.quantity : 1)) }}
                  </span>
                </div>
                <div v-if="product.discountType === '2x1'">
                  {{ currency(product.fullPrice * (product.quantity - product.discountAmount!)) }}
                </div>
                <div v-else>
                  {{ currency(product.finalPrice * (showFullQuantity ? product.quantity : 1)) }}
                </div>
              </div>
              <l-icon
                v-if="showBillingStatus && paymentIcon && iconColor"
                :name="paymentIcon"
                :class="iconColor"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-if="allowRemove && !disabled" #right>
      <div
        class="w-14 h-full flex items-center justify-center px-3 bg-r-500 cursor-pointer"
        @click="deleteProduct"
      >
        <l-icon name="delete" size="small" class="text-n-0" />
      </div>
    </template>
  </swipe-out>
  <product-discount v-if="showDiscount" :product="product" @close="closeSwipe" />
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { computed, ref, toRef, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { LIcon } from '@last/core-ui/paprika'

import SwipeOut from '@/components/SwipeOut.vue'
import { useMoney } from '@/composables/useMoney'
import useProduct from '@/composables/useProduct'
import ProductDiscount from '@/modules/checkout/ProductDiscount.vue'
import QuantitySelector from '@/modules/ordering/QuantitySelector.vue'
import { useCatalogStore } from '@/store/catalog'
import { useTabsStore } from '@/store/tabs'
import type { TabProduct } from '@/types'

const { t } = useI18n()

type Props = {
  product: TabProduct
  isSelected?: boolean
  allowRemove?: boolean
  allowDiscount?: boolean
  disabled?: boolean
  disableSwipe?: boolean
  showFullQuantity?: boolean
  showQuantitySelector?: boolean
  showSentToKitchen?: boolean
  showBillingStatus?: boolean
  showComments?: boolean
  showPrice?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isSelected: false,
  allowRemove: false,
  allowDiscount: false,
  disabled: false,
  disableSwipe: false,
  showQuantitySelector: false,
  showFullQuantity: true,
  showSentToKitchen: false,
  showBillingStatus: false,
  showComments: false,
  showPrice: true
})

const emit = defineEmits<{
  selected: []
  delete: []
}>()

const { currency } = useMoney()

const swipeOutRef = useTemplateRef('swipeOutRef')
const showDiscount = ref(false)

const product = toRef(props, 'product')

const {
  deleteProductWithConfirmation,
  updateQuantity,
  description,
  productIsPaid,
  productIsBilled
} = useProduct(product)

const tabStore = useTabsStore()
const { getProductById } = useCatalogStore()

const { getSentToKitchenTime } = tabStore

const editingQuantity = ref(false)

const paymentIcon = computed(() => {
  if (productIsPaid.value) return 'check-circle'
  if (productIsBilled.value) return 'inactive'
  return null
})

const iconColor = computed(() => {
  if (productIsPaid.value) return 'text-g-500'
  if (someProductBilled.value) return 'text-y-500'
  return null
})

const someProductBilled = computed(() => {
  return product.value.notBilledQuantity != product.value.quantity
})

const sentTime = computed(() => {
  return getSentToKitchenTime(product.value.id)
})

const productOutOfStock = computed(() => {
  const catalogProduct = getProductById(product.value.parentProduct!)
  return !(catalogProduct.hasStock ?? true)
})

function deleteProduct() {
  if (props.isSelected) {
    emit('selected')
  }
  deleteProductWithConfirmation()
}

function closeSwipe() {
  showDiscount.value = false
  swipeOutRef.value?.close()
}
</script>
